import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { PropId } from "building-types/props/propIds";
import type { AwningSettings } from "building-types/settings/awningSettings";
import type { ExteriorPropSettings } from "building-types/settings/exteriorPropSettings";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import { SettingsType } from "building-types/settings/settingsType";
import {
  AwningType,
  ColonnadeTerminationType,
  WallIntersectionType,
  WallRoofBaseBoardFilter,
  WallRoofBaseBoardMode,
} from "building-types/settings/settingsTypesEnum";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { BuiltinHomeSettingsId, builtinHomeSettingsPrettyName } from "./builtinHomeSettingsId";
import { defaultHomeSettings, defaultPorchSettings, defaultShedAwningSettings } from "./defaultSettings";

const redTreeAwningOptions: AwningSettings = {
  ...defaultShedAwningSettings,
  type: SettingsType.AWNING_SETTINGS,
  awningType: AwningType.SHED,
  slope: 1 / 2,
  depth: 25.5,
  fasciaHeight: 6,
  trimHeight: 2,
  trimWidth: 1,
  awningBracketPropId: BuiltInExteriorPropId.AWNINGBRACKET,
  bracketOffset: 2,
  bracketTrimSpacing: 3,
};

const porchSettings: PorchSettings = {
  ...defaultPorchSettings,
  type: SettingsType.PORCH_SETTINGS,
  endSegmentJoinTolerance: 60,
  minColumnSpacing: 60,
  maxColumnSpacing: 120,
  columnId: "DEFAULT",
  doorPathColumnSpread: 80,
  colonnadeTerminationRules: [
    {
      endType: WallIntersectionType.TEE,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
    {
      endType: WallIntersectionType.CORNER,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
    {
      endType: WallIntersectionType.CORNER_REVERSE,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
    {
      endType: WallIntersectionType.LINE,
      terminationType: ColonnadeTerminationType.COLUMN,
    },
  ],
  includeRailing: false,
  baseColumnOffset: true,
  internalColumnStructureSize: 3.5,
  internalHeaderStructureWidth: 3.5,
  headerCasingOffsetFromCenterLine: 1.375,
  headerCasingThickness: 8.25,
  columnCasingOffset: 0,
};

const redTreeExteriorPropSettings: ExteriorPropSettings = {
  type: SettingsType.EXTERIOR_PROP_SETTINGS,
  propTypes: {
    ...defaultExteriorPropCatalog,
    garageDoorSingle: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
      propAssetId: PropId.DoorGar_D16x7_2x1Pattern_1Lite3rdCross_01,
    },
    garageDoorDouble: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
      propAssetId: PropId.DoorGar_D16x7_2x1Pattern_1Lite3rdCross_01,
    },
    doorSingleGlass: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
      propAssetId: PropId.DoorExt_Single36_02,
    },
    doorSingleGlassLow: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
      propAssetId: PropId.DoorExt_Single36_01,
    },
  },
};

const redTreeMaterials: { [id: string]: HomeSettings } = {
  [BuiltinHomeSettingsId.RedTree_001]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.RedTree_001],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.RedTree_003 + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.RedTree_001],
    },
    exteriorProps: redTreeExteriorPropSettings,

    exteriorTrim: {
      type: SettingsType.EXTERIOR_TRIM_SETTINGS,
      enableOutsideCornerMoulding: true,
      enableInsideCornerMoulding: false,
      wallRoofBaseBoard: WallRoofBaseBoardMode.NONE,
      wallRoofBaseBoardFilter: WallRoofBaseBoardFilter.ALL_EDGES,
    },
    porch: porchSettings,
    awning: redTreeAwningOptions,
  },
  [BuiltinHomeSettingsId.RedTree_002]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.RedTree_002],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.RedTree_003 + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.RedTree_002],
    },
    exteriorProps: redTreeExteriorPropSettings,
    exteriorTrim: {
      type: SettingsType.EXTERIOR_TRIM_SETTINGS,
      enableOutsideCornerMoulding: false,
      enableInsideCornerMoulding: false,
      wallRoofBaseBoard: WallRoofBaseBoardMode.NONE,
      wallRoofBaseBoardFilter: WallRoofBaseBoardFilter.ALL_EDGES,
    },
    porch: porchSettings,
    awning: redTreeAwningOptions,
  },
  [BuiltinHomeSettingsId.RedTree_003]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.RedTree_003],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.RedTree_003 + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.RedTree_003],
    },
    exteriorProps: redTreeExteriorPropSettings,
    exteriorTrim: {
      type: SettingsType.EXTERIOR_TRIM_SETTINGS,
      enableOutsideCornerMoulding: false,
      enableInsideCornerMoulding: false,
      wallRoofBaseBoard: WallRoofBaseBoardMode.NONE,
      wallRoofBaseBoardFilter: WallRoofBaseBoardFilter.ALL_EDGES,
    },
    porch: porchSettings,
    awning: redTreeAwningOptions,
  },
};

export default redTreeMaterials;
